// src/AppHeader.js
// ============================================================
// Your Scholarship Finder — Persistent App Header
// ============================================================
// Shared header across parent and student dashboards.
// Shows brand, user name, Logout. Account link shown only for
// subscription owners (active or canceling).
// Dashboard button added S51 — gives parents/students an
// explicit nav back to their dashboard from Help, Account, etc.
// ============================================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

export default function AppHeader({ subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAccount, setShowAccount] = useState(false);

  // Check if user owns a subscription (show Account button only for CC holders)
  useEffect(() => {
    if (!user) return;
    const checkSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/subscription-status`, {
          headers: { 'Authorization': `Bearer ${user.token}` },
        });
        if (res.ok) {
          const data = await res.json();
          // Show Account for active subscribers or those in canceling state
          setShowAccount(data.hasSubscription || data.status === 'canceled');
        }
      } catch (err) {
        // Silently fail — just don't show Account button
      }
    };
    checkSubscription();
  }, [user]);

  // Don't render if no authenticated user (demo/API-secret views)
  if (!user) return null;

  const isAccountPage = location.pathname === '/account';

  // Dashboard path is role-dependent
  const dashboardPath = user.role === 'parent' ? '/parent/dashboard' : '/';
  const isDashboardPage = user.role === 'parent'
    ? location.pathname === '/parent/dashboard'
    : location.pathname === '/' || location.pathname.startsWith('/dashboard');

  return (
    <div style={styles.header}>
      <div style={styles.inner}>
        {/* Left: Brand */}
        <button
          onClick={() => navigate(dashboardPath)}
          style={styles.brandBtn}
          title="Home"
        >
            <svg width="32" height="32" viewBox="0 0 64 64" aria-label="Your Scholarship Finder" style={{ flexShrink: 0 }}>
            <rect width="64" height="64" rx="16" fill="#10314F"/>
            <path d="M32 47 C32 47 20 34.5 20 26 A12 12 0 1 1 44 26 C44 34.5 32 47 32 47 Z" fill="none" stroke="#FFFFFF" strokeWidth="4.2" strokeLinejoin="round"/>
            <circle cx="32" cy="26" r="4.8" fill="#EF9F27"/>
          </svg>
          <div style={styles.brandText}>
            <div style={styles.brandName}><span style={{ fontStyle: 'italic', fontWeight: 500, opacity: 0.85 }}>Your</span> Scholarship Finder</div>
            {subtitle && <div style={styles.brandSub}>{subtitle}</div>}
          </div>
        </button>

        {/* Right: Nav items */}
        <div style={styles.navRight}>
          <span style={styles.userName}>{user.full_name}</span>
          <button
            onClick={() => navigate(dashboardPath)}
            style={{
              ...styles.navBtn,
              ...(isDashboardPage ? styles.navBtnActive : {}),
            }}
          >
            Dashboard
          </button>
          <button
              onClick={() => navigate('/faq')}
              style={{
                ...styles.navBtn,
                ...(location.pathname === '/faq' ? styles.navBtnActive : {}),
              }}
            >
              Help
            </button>
          {showAccount && (
            <button
              onClick={() => navigate('/account')}
              style={{
                ...styles.navBtn,
                ...(isAccountPage ? styles.navBtnActive : {}),
              }}
            >
              Account
            </button>
          )}
          <button onClick={logout} style={styles.navBtn}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: {
    background: '#1F4E79',
    padding: '0 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  inner: {
    maxWidth: '1000px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '56px',
  },
  brandBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  
  brandText: {
    textAlign: 'left',
  },
  brandName: {
    color: 'white',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.2,
  },
  brandSub: {
    color: '#AED6F1',
    fontSize: '11px',
    lineHeight: 1.2,
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userName: {
    color: '#AED6F1',
    fontSize: '13px',
    fontWeight: 500,
    marginRight: '4px',
  },
  navBtn: {
    fontSize: '12px',
    padding: '6px 14px',
    borderRadius: '6px',
    background: '#1a3a5c',
    color: 'white',
    border: '1px solid #2E75B6',
    cursor: 'pointer',
    fontWeight: 600,
  },
  navBtnActive: {
    background: '#2E75B6',
    borderColor: '#5DADE2',
  },
};
